/* Copyright 2016-2022
   Kabandy
   Author: Cedric Hoarau
 */

import { ApiDataModel } from "../ApiDataModel";
import { ApiRequestHandler, getApiKey } from "../ApiWeb";

type IApiBoard = ApiDataModel.IBoard;
type IApiBoardStatus = ApiDataModel.IBoardStatus;

let _rootDiv: HTMLDivElement | undefined = undefined;
let _currentBoardsHtmlListElement: HTMLUListElement | undefined = undefined;
let _currentSelectedBoardHtmlDivElement: HTMLDivElement | undefined = undefined;

let _accessToken: string = "";
let _onBoardSelected: Function | undefined = undefined;
let _currentBoards: IApiBoard[] = [];
let _currentBoardStatuses: IApiBoardStatus[] = [];

export module UIBoard {
    export function Bind(accessToken: string, workspaceId: string, onBoardSelected: Function): void {
        _accessToken = accessToken;
        _onBoardSelected = onBoardSelected;

        _rootDiv = _InitializeUI(accessToken, workspaceId);
        document.body.appendChild(_rootDiv);
    }
    export function Unbind() {
        if (_rootDiv) {
            _rootDiv.remove();
        }

        _currentBoards = [];
        _currentBoardStatuses = [];

        if (_currentBoardsHtmlListElement) {
            _currentBoardsHtmlListElement = undefined;
        }

        if (_currentSelectedBoardHtmlDivElement) {
            _currentSelectedBoardHtmlDivElement.style.fontWeight = "";
            _currentSelectedBoardHtmlDivElement.style.textDecoration = "";
        }
    }

    function _InitializeUI(accessToken: string, workspaceId: string): HTMLDivElement {
        let rootDiv = document.createElement("div");
        rootDiv.id = "ui-boards-root-div";

        // title
        let titleElement = document.createElement("h2");
        titleElement.innerText = "Boards";
        rootDiv.appendChild(titleElement);

        // get button
        let getButton = document.createElement("button");
        getButton.id = "ui-boards-get-button";
        getButton.textContent = "Get Boards";
        rootDiv.appendChild(getButton);
        rootDiv.appendChild(document.createElement("br"));
        getButton.addEventListener("click", async () => {
            _currentBoards = await _FetchBoards(accessToken, workspaceId);
            _currentBoardStatuses = await _FetchStatusesFromBoards(accessToken, _currentBoards);
            _currentBoardsHtmlListElement = _RefreshUIBoards(_currentBoardsHtmlListElement, _currentBoards, _currentBoardStatuses);
        });

        // name input
        let nameInput = document.createElement("input");
        nameInput.id = "ui-boards-create-input";
        let nameLabelElement = document.createElement("label");
        nameLabelElement.textContent = "Board name";
        nameLabelElement.setAttribute("for", nameInput.id);
        rootDiv.appendChild(nameLabelElement);
        rootDiv.appendChild(nameInput);

        // create button
        let createButton = document.createElement("button");
        createButton.id = "ui-boards-create-button";
        createButton.textContent = "Create Board";
        rootDiv.appendChild(createButton);
        createButton.addEventListener("click", async () => {
            if (nameInput) {
                // @ts-ignore
                const sanitizedValue: string = DOMPurify.sanitize(nameInput.value, { USE_PROFILES: { html: false } });
                await _CreateBoard(accessToken, workspaceId, sanitizedValue);
                _currentBoards = await _FetchBoards(accessToken, workspaceId);
                _currentBoardStatuses = await _FetchStatusesFromBoards(accessToken, _currentBoards);
                _currentBoardsHtmlListElement = _RefreshUIBoards(_currentBoardsHtmlListElement, _currentBoards, _currentBoardStatuses);
            }
        });

        // update button
        const updateButton: HTMLButtonElement = document.createElement("button");
        updateButton.id = "ui-boards-update-button";
        updateButton.textContent = "Update Board";
        rootDiv.appendChild(updateButton);
        updateButton.addEventListener("click", async () => {
            if (!_currentSelectedBoardHtmlDivElement) {
                throw new Error("Invalid/Missing current selected board");
            }

            // @ts-ignore
            const sanitizedValue: string = DOMPurify.sanitize(nameInput.value, { USE_PROFILES: { html: false } });
            const apiBoard: IApiBoard = await _UpdateBoard(accessToken, workspaceId, _currentSelectedBoardHtmlDivElement.id, sanitizedValue); 
            const apiBoardStatus: IApiBoardStatus = await _FetchStatusFromBoard( accessToken, apiBoard );
            _UpdateUIBoardsFromBoard(apiBoard, apiBoardStatus);
        });

        // list of boards
        _currentBoardsHtmlListElement = _CreateUIBoards(_currentBoards);
        rootDiv.appendChild(_currentBoardsHtmlListElement);

        return rootDiv;
    }
    function _CreateUIBoards(apiBoards: IApiBoard[] = [], apiBoardStatuses: IApiBoardStatus[] = []): HTMLUListElement {
        const oList = document.createElement("ul");

        const sortedApiBoards: IApiBoard[] = apiBoards.sort(
            (apiBoardA, apiBoardB) => new Date(apiBoardA.created_at).getTime() - new Date(apiBoardB.created_at).getTime()
        );

        for (let cApiBoard of sortedApiBoards) {
            let cListElement: HTMLLIElement = document.createElement("li");
            cListElement.style.display = "flex";
            cListElement.style.alignItems = "center";

            let cBoardId: string = cApiBoard.id;
            let cBoardName: string = cApiBoard.name;
            let cWorkspaceId: string = cApiBoard.workspaceId;
            let cBoardCreatedAt: string = cApiBoard.created_at;
            let cBoardUpdatedAt: string = cApiBoard.updated_at;

            let cStatusAsStr: string = "";
            let cStatus: IApiBoardStatus | undefined = apiBoardStatuses.find(status => cBoardId === status.boardId);
            if (cStatus) {
                cStatusAsStr = cStatus.value;
            }

            let cBoardDiv: HTMLDivElement = document.createElement("div");
            cBoardDiv.id = cBoardId;
            cBoardDiv.textContent = `board ${cBoardId} | ${cBoardName} | created at: ${cBoardCreatedAt} | updated at: ${cBoardUpdatedAt} | status: ${cStatusAsStr}`;
            cListElement.appendChild(cBoardDiv);
            cBoardDiv.addEventListener("click", () => {
                // Remove bold + underline
                if (_currentSelectedBoardHtmlDivElement) {
                    _currentSelectedBoardHtmlDivElement.style.fontWeight = "";
                    _currentSelectedBoardHtmlDivElement.style.textDecoration = "";
                }

                // Add bold + underline
                cBoardDiv.style.fontWeight = "bold";
                cBoardDiv.style.textDecoration = "underline";

                // Swap selected board
                _currentSelectedBoardHtmlDivElement = cBoardDiv;

                // Call next
                if (_onBoardSelected) {
                    _onBoardSelected(_accessToken, cWorkspaceId, cBoardId);
                }
            });

            // get button
            let cGetButton: HTMLButtonElement = document.createElement("button");
            cGetButton.id = "ui-boards-get-button-" + cBoardId;
            cGetButton.textContent = "Get";
            cGetButton.style.marginLeft = "10px";
            cListElement.appendChild(cGetButton);
            cGetButton.addEventListener("click", async () => {
                const [apiBoard, apiBoardStatus]: [ IApiBoard, IApiBoardStatus ]  = await Promise.all( [ 
                    _FetchBoard(_accessToken, cWorkspaceId, cBoardId),
                    _FetchStatusFromBoard(_accessToken, cApiBoard)
                ]);
                _UpdateUIBoardsFromBoard(apiBoard, apiBoardStatus);
            });

            // delete button
            let cDeleteButton: HTMLButtonElement = document.createElement("button");
            cDeleteButton.id = "ui-boards-delete-button-" + cBoardId;
            cDeleteButton.textContent = "Delete";
            cDeleteButton.style.marginLeft = "10px";
            cListElement.appendChild(cDeleteButton);
            cDeleteButton.addEventListener("click", async () => {
                await _DeleteBoard(_accessToken, cWorkspaceId, cBoardId);
                _currentBoards = await _FetchBoards(_accessToken, cWorkspaceId);
                _currentBoardStatuses = await _FetchStatusesFromBoards(_accessToken, _currentBoards);
                _currentBoardsHtmlListElement = _RefreshUIBoards(_currentBoardsHtmlListElement, _currentBoards, _currentBoardStatuses);
            });

            oList.appendChild(cListElement);
        }

        return oList;
    }
    function _RefreshUIBoards(currentList?: HTMLUListElement, apiBoards: IApiBoard[] = [], apiBoardStatuses: IApiBoardStatus[] = []): HTMLUListElement | undefined {
        let oNextList: HTMLUListElement | undefined = undefined;

        if (currentList) {
            const nextList = _CreateUIBoards(apiBoards, apiBoardStatuses);

            const parentElement: HTMLElement | null = currentList.parentElement;
            if (parentElement) {
                parentElement.replaceChild(nextList, currentList);
                oNextList = nextList;
            }
        }

        return oNextList;
    }
    function _UpdateUIBoardsFromBoard(apiBoard: IApiBoard, apiBoardStatus: IApiBoardStatus ): void {
        const updatedStatuses: IApiBoardStatus[] = _currentBoardStatuses.map(cBoardStatus =>
            apiBoardStatus.id === cBoardStatus.id ? apiBoardStatus : cBoardStatus
        );
        const updatedBoards: IApiBoard[] = _currentBoards.map(cBoard => (apiBoard.id === cBoard.id ? apiBoard : cBoard));
        _currentBoardsHtmlListElement = _RefreshUIBoards(_currentBoardsHtmlListElement, updatedBoards, updatedStatuses);

        if (_currentSelectedBoardHtmlDivElement) {
            _currentSelectedBoardHtmlDivElement.style.fontWeight = "";
            _currentSelectedBoardHtmlDivElement.style.textDecoration = "";
            _currentSelectedBoardHtmlDivElement = undefined;
        }
    }

    async function _CreateBoard(accessToken: string, workspaceId: string, name: string): Promise<IApiBoard> {
        const board: IApiBoard = await ApiRequestHandler.PostMeBoards( getApiKey(), accessToken, workspaceId, { name: name } );
        return board;
    }
    async function _FetchBoards(accessToken: string, workspaceId: string): Promise<IApiBoard[]> {
        const boards : IApiBoard[] = await ApiRequestHandler.GetMeBoards(getApiKey(), accessToken, workspaceId );
        return boards || [];
    }
    async function _FetchBoard(accessToken: string, workspaceId: string, boardId: string): Promise<IApiBoard> {
        const board: IApiBoard = await ApiRequestHandler.GetMeBoard(getApiKey(), accessToken, workspaceId, boardId );
        return board;
    }
    async function _UpdateBoard(accessToken: string, workspaceId: string, boardId: string, name: string): Promise<IApiBoard> {
        const board: IApiBoard = await ApiRequestHandler.PutMeBoard(getApiKey(), accessToken, workspaceId, boardId, { name: name } );
        return board;
    }
    async function _DeleteBoard(accessToken: string, workspaceId: string, boardId: string): Promise<void> {
        await ApiRequestHandler.DeleteMeBoard(getApiKey(), accessToken, workspaceId, boardId );
    }

    //
    // --- Statuses

    async function _FetchStatusesFromBoards(accessToken: string, boards: IApiBoard[] = []): Promise<IApiBoardStatus[]> {
        const boardStatuses: IApiBoardStatus[] = await ApiRequestHandler.GetMeBoardStatuses(getApiKey(), accessToken, boards );
        return boardStatuses;
    }
    async function _FetchStatusFromBoard( accessToken: string, board: IApiBoard): Promise<IApiBoardStatus> {
        const boardStatus: IApiBoardStatus = await ApiRequestHandler.GetMeBoardStatus( getApiKey(), accessToken, board );
        return boardStatus;
    }
}
